import { ComponentType } from 'react';

import { MessageDescriptor, defineMessages } from 'react-intl';

import { useJsmPlaygroundSignupHref } from '@/components/signup/hooks/use-jsm-playground-signup-href';
import { ProductKey } from '@/components/signup/utils/products';
import {
	useLoggedInHasConfluence,
	useLoggedInHasJSM,
} from '@/components/user-context/helpers/cohorts';
import { getProductIntent } from '@/components/user-context/helpers/use-product-intent';
import { ConfluenceMarkBrandV3 } from '@/libraries/wpl/foundations/src/logos-v3/confluence-v3-mark-brand';
import { JiraServiceManagementMarkBrandV3 } from '@/libraries/wpl/foundations/src/logos-v3/jira-service-management-v3-mark-brand';
import { delocalizePathname } from '@/utils/localization';

/**
 * Products supported by the generic logged-in hero section. Adding a new
 * product is a single entry in {@link PRODUCT_CONFIG} plus a thin wrapper in
 * `index.tsx` — no new Uniform-authored properties are required, the product is
 * resolved entirely in code.
 */
export type LoggedInHeroProduct = 'jsm' | 'confluence';

/**
 * A pass-through "signup href" hook for products that don't need any special
 * href resolution. Kept as a hook so every product resolves its create/try link
 * through the same call shape (and the rules-of-hooks stay satisfied).
 */
const useIdentityHref = (href: string): string => href;

/**
 * How to build the "go to your existing site" deep link for a product.
 *
 * The site host always comes from the user's real site URL, so staging
 * (`*.jira-dev.com`) and production (`*.atlassian.net`) are both handled
 * without any environment-specific logic.
 *
 * Two shapes are supported, matching what each product's logged-in hero does
 * today:
 *  - **Jira / JSM** — a login bounce: `/login.jsp?os_destination=<site>/browse`
 *    (`loginPath` set). Preserves the original JSM behaviour.
 *  - **Confluence** — a plain path append: `<site>/wiki` (`loginPath` omitted),
 *    matching `ReturnToProduct`'s `getProductUrl` for Confluence.
 *
 * Historically this was hardcoded to the Jira form, which sent Confluence
 * users to the Jira `/browse` path of their (Confluence-named) site.
 */
export interface SiteLoginConfig {
	/** Pathname the user lands on, e.g. `/browse` (Jira) or `/wiki` (Confluence). */
	destinationPath: string;
	/**
	 * Optional login endpoint to bounce through, e.g. `/login.jsp`. When set,
	 * the URL becomes `<site>{loginPath}?os_destination=<site>{destinationPath}`.
	 * When omitted, the URL is simply `<site>{destinationPath}`.
	 */
	loginPath?: string;
}

export interface ProductMessages {
	/** Display name of the product, e.g. "Jira Service Management". */
	productName: string;
	/** Label for the primary "Go to <product>" CTA button. */
	goToProduct: MessageDescriptor;
}

export interface LoggedInHeroProductConfig {
	/** Cohort hook that gates whether the hero renders for this product. */
	useHasProduct: () => boolean | undefined;
	/**
	 * Delocalized pathname prefixes this product "owns". Used to resolve the
	 * product from the current URL (see {@link resolveProductFromPathname}) so a
	 * single hero registration can serve multiple product landing pages without
	 * the product being hardcoded per page.
	 */
	pathMatchers: string[];
	/** `productKey` substring used to filter the user's recent sites. */
	productKeyFilter: string;
	/** Product passed to the recent-work activity client. */
	activityClientProduct: string;
	/** How to build the "go to your existing site" login deep link. */
	siteLogin: SiteLoginConfig;
	/** Brand mark rendered inline in the subheading. */
	LogoMark: ComponentType;
	/** Link used by the "Go to <product>" primary CTA fallback / create flow. */
	createNewSiteHref: string;
	/** Link used by the "Looking for a different workspace?" CTA. */
	existingSitesHref: string;
	/** Optional hook to further resolve the create/try href (e.g. JSM playground intent). */
	useResolvedCreateHref: (href: string) => string;
	/** Analytics `actionSubjectId` for click events. */
	analyticsActionSubjectId: string;
	/** Analytics `source` for click events. */
	analyticsSource: string;
	/** Localised copy. */
	messages: ProductMessages;
}

const goToProductMessages = defineMessages({
	jsm: {
		id: 'JiraServiceManagementPageTemplate.loggedInHero.goToJsm',
		defaultMessage: 'Go to Jira Service Management',
	},
	confluence: {
		id: 'LoggedInHeroSection.goToConfluence',
		defaultMessage: 'Go to Confluence',
	},
});

export const PRODUCT_CONFIG: Record<LoggedInHeroProduct, LoggedInHeroProductConfig> = {
	// JSM config preserves the exact behaviour of the original component so the
	// existing `jsmLoggedInHeroSection` registration is unchanged.
	jsm: {
		useHasProduct: useLoggedInHasJSM,
		pathMatchers: ['/software/jira-service-management', '/software/jira/service-management'],
		productKeyFilter: 'jira-servicedesk.ondemand',
		activityClientProduct: 'jira',
		siteLogin: { loginPath: '/login.jsp', destinationPath: '/browse' },
		LogoMark: JiraServiceManagementMarkBrandV3,
		createNewSiteHref:
			'/try/cloud/signup?bundle=service-collection&createNew=true&primaryCollectionProduct=jira-servicedesk.ondemand',
		existingSitesHref:
			'/try/cloud/signup?bundle=service-collection&primaryCollectionProduct=jira-servicedesk.ondemand',
		useResolvedCreateHref: useJsmPlaygroundSignupHref,
		analyticsActionSubjectId: 'jsmLoggedInHero',
		analyticsSource: '/software/jira-service-management',
		messages: {
			productName: 'Jira Service Management',
			goToProduct: goToProductMessages.jsm,
		},
	},
	confluence: {
		useHasProduct: useLoggedInHasConfluence,
		pathMatchers: ['/software/confluence'],
		productKeyFilter: 'confluence.ondemand',
		activityClientProduct: 'confluence',
		// Confluence lands directly on `<site>/wiki` (no login bounce), matching the
		// canonical ReturnToProduct.getProductUrl convention used elsewhere in WAC.
		siteLogin: { destinationPath: '/wiki' },
		LogoMark: ConfluenceMarkBrandV3,
		createNewSiteHref:
			'/try/cloud/signup?bundle=confluence&createNew=true&primaryCollectionProduct=confluence.ondemand',
		existingSitesHref:
			'/try/cloud/signup?bundle=confluence&primaryCollectionProduct=confluence.ondemand',
		useResolvedCreateHref: useIdentityHref,
		analyticsActionSubjectId: 'confluenceLoggedInHero',
		analyticsSource: '/software/confluence',
		messages: {
			productName: 'Confluence',
			goToProduct: goToProductMessages.confluence,
		},
	},
};

/**
 * Fallback product used when the current URL doesn't match any product's
 * {@link LoggedInHeroProductConfig.pathMatchers}. Kept as JSM so the original
 * behaviour is preserved for any page that doesn't opt into URL detection.
 */
export const DEFAULT_LOGGED_IN_HERO_PRODUCT: LoggedInHeroProduct = 'jsm';

/**
 * Resolves the hero product from a (possibly locale-prefixed) pathname by
 * matching it against each product's `pathMatchers`. The locale prefix is
 * stripped first so `/fr/software/confluence` resolves the same as
 * `/software/confluence`. Longer matchers win, so a more specific path like
 * `/software/jira/service-management` takes precedence over a broader one.
 *
 * Returns `undefined` when nothing matches, letting callers decide on a
 * fallback (see {@link DEFAULT_LOGGED_IN_HERO_PRODUCT}).
 */
export const resolveProductFromPathname = (
	pathname: string | undefined,
): LoggedInHeroProduct | undefined => {
	if (!pathname) {
		return undefined;
	}

	const normalized = delocalizePathname(pathname);

	let bestMatch: { product: LoggedInHeroProduct; length: number } | undefined;
	for (const product of Object.keys(PRODUCT_CONFIG) as LoggedInHeroProduct[]) {
		for (const matcher of PRODUCT_CONFIG[product].pathMatchers) {
			const matches = normalized === matcher || normalized.startsWith(`${matcher}/`);
			if (matches && (!bestMatch || matcher.length > bestMatch.length)) {
				bestMatch = { product, length: matcher.length };
			}
		}
	}

	return bestMatch?.product;
};

/**
 * Maps a signup {@link ProductKey} (as returned by
 * {@link getProductIntent}) down to the narrower set of products the logged-in
 * hero supports. Returns `undefined` for products the hero doesn't render.
 *
 * Note the JSM landing path resolves to the **Service Collection** product in
 * `getProductIntent`, so both that and a bare JSM key map to the hero's `jsm`.
 */
const mapProductIntentToHeroProduct = (
	productKey: string | undefined,
): LoggedInHeroProduct | undefined => {
	switch (productKey) {
		case ProductKey.CONFLUENCE:
			return 'confluence';
		case ProductKey.JIRA_SERVICE_MANAGEMENT:
		case ProductKey.SERVICE_COLLECTION:
			return 'jsm';
		default:
			return undefined;
	}
};

/**
 * Query params the Uniform preview route uses to carry the *intended* page.
 *
 * On the `/uniform-preview` route the browser pathname is always
 * `/uniform-preview`, so the real landing-page path (e.g. `/software/confluence`)
 * is passed as a `path` query param instead — see the staging preview URL the
 * Uniform authoring tool generates. `slug` carries the bare page slug
 * (e.g. `confluence`) as a secondary signal.
 */
const UNIFORM_PREVIEW_PATH_PARAM = 'path';
const UNIFORM_PREVIEW_SLUG_PARAM = 'slug';

/** Maps a bare Uniform `slug` (e.g. `confluence`) to a hero product. */
const resolveProductFromSlug = (slug: string | null): LoggedInHeroProduct | undefined => {
	if (!slug) {
		return undefined;
	}
	// A slug is a bare page name; reuse the pathname matchers by prefixing the
	// marketing namespace so `confluence` resolves the same as
	// `/software/confluence`.
	return resolveProductFromPathname(`/software/${slug.replace(/^\/+/, '')}`);
};

/**
 * Resolves the hero product from a full {@link Location}.
 *
 * Resolution order:
 *  1. **Primary — {@link getProductIntent}**: the same path→product logic the
 *     embedded signup / product tour uses, so the hero resolves its product the
 *     way the rest of the signup surface does. Reads the *real* pathname.
 *  2. **Uniform preview fallback**: on `/uniform-preview` the real pathname is
 *     `/uniform-preview` (so step 1 can't resolve), and the intended page is
 *     carried as a `path` (and `slug`) query param instead. These are honoured
 *     only as a fallback, keeping `getProductIntent` the primary method.
 *
 * Returns `undefined` when nothing resolves, letting callers fall back to
 * {@link DEFAULT_LOGGED_IN_HERO_PRODUCT}.
 */
export const resolveProductFromLocation = (
	location: Pick<Location, 'href' | 'pathname' | 'search'> | undefined,
): LoggedInHeroProduct | undefined => {
	if (!location) {
		return undefined;
	}

	// 1. Primary: reuse the signup/product-tour intent logic.
	const fromIntent = mapProductIntentToHeroProduct(getProductIntent(location.href).product?.key);
	if (fromIntent) {
		return fromIntent;
	}

	// 2. Uniform preview fallback: the intended page is in query params because
	//    the real pathname is `/uniform-preview`.
	const params = new URLSearchParams(location.search);
	const fromPreviewPath = resolveProductFromPathname(
		params.get(UNIFORM_PREVIEW_PATH_PARAM) ?? undefined,
	);
	if (fromPreviewPath) {
		return fromPreviewPath;
	}

	return resolveProductFromSlug(params.get(UNIFORM_PREVIEW_SLUG_PARAM));
};
